import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'

import {
  downloadApplicationPdf,
  downloadPaperIndemnity,
  downloadPaymentProof,
  fetchApplication,
  updateApplication,
  uploadPaperIndemnity,
  uploadPaymentProof,
  type Application,
} from '@/api/applicationsApi'
import { ApiClientError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

type EditForm = {
  applicant_name: string
  business_name: string
  email: string
  cellphone: string
  items_for_sale: string
  vendor_category: 'commercial' | 'school_community' | 'learner_classroom'
  supervising_teacher: string
  identity_number: string
  stall_number: string
  electricity_needed: boolean
  fee_amount: string
  signatory_name: string
  signed_on: string
  status: 'submitted' | 'approved' | 'rejected'
  admin_notes: string
  content_approved: boolean
}

function toForm(app: Application): EditForm {
  return {
    applicant_name: app.applicant_name,
    business_name: app.business_name,
    email: app.email,
    cellphone: app.cellphone,
    items_for_sale: app.items_for_sale || '',
    vendor_category: (app.vendor_category || 'commercial') as EditForm['vendor_category'],
    supervising_teacher: app.supervising_teacher || '',
    identity_number: app.identity_number || '',
    stall_number: app.stall_number || '',
    electricity_needed: app.electricity_needed,
    fee_amount: app.fee_amount != null ? String(app.fee_amount) : '',
    signatory_name: app.signatory_name || '',
    signed_on: app.signed_on || '',
    status: app.status,
    admin_notes: app.admin_notes || '',
    content_approved: Boolean(app.content_approved),
  }
}

type UploadKind = 'pop' | 'indemnity'

/**
 * Admin detail + moderation editor for one vendor application.
 * Content (especially items for sale) must be approved before any public listing.
 */
export function AdminApplicationDetailPage() {
  const { id } = useParams()
  const { isAuthenticated, isAdmin } = useAuth()
  const [application, setApplication] = useState<Application | null>(null)
  const [form, setForm] = useState<EditForm | null>(null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [uploadKind, setUploadKind] = useState<UploadKind>('pop')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!id) return
    void (async () => {
      try {
        const result = await fetchApplication(Number(id))
        setApplication(result.application)
        setForm(toForm(result.application))
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load application.')
      }
    })()
  }, [id])

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/vendor" replace />

  function updateField<K extends keyof EditForm>(key: K, value: EditForm[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current))
    setSaved('')
  }

  const formLooksComplete = Boolean(
    form &&
      form.applicant_name.trim() &&
      form.business_name.trim() &&
      form.email.trim() &&
      form.cellphone.trim() &&
      form.items_for_sale.trim() &&
      form.signatory_name.trim() &&
      form.signed_on,
  )

  const hasIndemnity = Boolean(
    application && (application.has_signature || application.has_paper_indemnity),
  )

  const canApproveNow = Boolean(
    application && formLooksComplete && hasIndemnity && application.has_payment_proof,
  )

  async function onSave(event: FormEvent) {
    event.preventDefault()
    if (!application || !form) return
    setBusy(true)
    setError('')
    setSaved('')
    try {
      if (form.status === 'approved' && !canApproveNow) {
        throw new ApiClientError(
          'Cannot approve stall until the form is complete, indemnity is on file (signature or paper scan), and proof of payment is uploaded.',
          400,
          'not_ready_for_approval',
        )
      }
      const fee = form.fee_amount.trim() === '' ? null : Number(form.fee_amount)
      if (fee != null && Number.isNaN(fee)) {
        throw new ApiClientError('Fee must be a number.', 400, 'invalid_request')
      }
      const result = await updateApplication(application.id, {
        applicant_name: form.applicant_name,
        business_name: form.business_name,
        email: form.email,
        cellphone: form.cellphone,
        items_for_sale: form.items_for_sale,
        vendor_category: form.vendor_category,
        supervising_teacher:
          form.vendor_category === 'learner_classroom' ? form.supervising_teacher : null,
        identity_number: form.identity_number || null,
        stall_number: form.stall_number || null,
        electricity_needed: form.electricity_needed,
        fee_amount: fee,
        signatory_name: form.signatory_name,
        signed_on: form.signed_on,
        status: form.status,
        admin_notes: form.admin_notes || null,
        content_approved: form.content_approved,
      })
      setApplication(result.application)
      setForm(toForm(result.application))
      setSaved('Application saved. Public listing will only use content marked as approved.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save application.')
    } finally {
      setBusy(false)
    }
  }

  function openUploadPicker(kind: UploadKind) {
    setUploadKind(kind)
    setError('')
    setSaved('')
    fileInputRef.current?.click()
  }

  async function onFileSelected(fileList: FileList | null) {
    const file = fileList?.[0]
    const kind = uploadKind
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!application || !file) return

    setBusy(true)
    setError('')
    setSaved('')
    try {
      const result =
        kind === 'indemnity'
          ? await uploadPaperIndemnity(application.id, file)
          : await uploadPaymentProof(application.id, file)
      setApplication(result.application)
      setForm(toForm(result.application))
      if (kind === 'indemnity') {
        setSaved(
          result.application.has_paper_indemnity
            ? `Paper indemnity uploaded${
                result.application.paper_indemnity_original_name
                  ? `: ${result.application.paper_indemnity_original_name}`
                  : ''
              }.`
            : 'Paper indemnity uploaded.',
        )
      } else {
        setSaved(
          result.application.has_payment_proof
            ? `Proof of payment uploaded${
                result.application.payment_proof_original_name
                  ? `: ${result.application.payment_proof_original_name}`
                  : ''
              }.`
            : 'Proof of payment uploaded.',
        )
      }
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : kind === 'indemnity'
            ? 'Paper indemnity upload failed.'
            : 'Proof of payment upload failed.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Application #{id}</h1>
          <p className={styles.lead}>
            Edit and moderate vendor details. Upload a paper indemnity scan when the booking came from a
            physical form. Products for sale stay private until you mark content as approved.
          </p>
        </div>
        <Link className={styles.ghost} to="/admin">
          Back to list
        </Link>
      </header>

      {error ? <p className={styles.error}>{error}</p> : null}
      {saved ? <p className={styles.meta}>{saved}</p> : null}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => void onFileSelected(event.target.files)}
      />

      {!application || !form ? (
        <p className={styles.empty}>Loading…</p>
      ) : (
        <>
          <form className={styles.formGrid} onSubmit={onSave}>
            <p className={styles.meta}>
              Market: <strong>{application.market_title}</strong>
              {application.market_event_date ? ` · ${application.market_event_date}` : ''}
            </p>

            <label className={authStyles.label}>
              Applicant name
              <input
                className={authStyles.input}
                value={form.applicant_name}
                onChange={(e) => updateField('applicant_name', e.target.value)}
                required
              />
            </label>

            <label className={authStyles.label}>
              Business name
              <input
                className={authStyles.input}
                value={form.business_name}
                onChange={(e) => updateField('business_name', e.target.value)}
                required
              />
            </label>

            <label className={authStyles.label}>
              Email
              <input
                className={authStyles.input}
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                required
              />
            </label>

            <label className={authStyles.label}>
              Cellphone
              <input
                className={authStyles.input}
                value={form.cellphone}
                onChange={(e) => updateField('cellphone', e.target.value)}
                required
              />
            </label>

            <label className={authStyles.label}>
              Vendor category
              <select
                className={authStyles.select}
                value={form.vendor_category}
                onChange={(e) =>
                  updateField('vendor_category', e.target.value as EditForm['vendor_category'])
                }
              >
                <option value="commercial">Commercial Vendor</option>
                <option value="school_community">School Community Vendor</option>
                <option value="learner_classroom">Learner / Classroom Market</option>
              </select>
            </label>

            {form.vendor_category === 'learner_classroom' ? (
              <label className={authStyles.label}>
                Supervising teacher
                <input
                  className={authStyles.input}
                  value={form.supervising_teacher}
                  onChange={(e) => updateField('supervising_teacher', e.target.value)}
                  required
                />
              </label>
            ) : null}

            <label className={authStyles.label}>
              Identity / registration number
              <input
                className={authStyles.input}
                value={form.identity_number}
                onChange={(e) => updateField('identity_number', e.target.value)}
              />
            </label>

            <label className={authStyles.label}>
              Stall number (allocated by organiser)
              <input
                className={authStyles.input}
                value={form.stall_number}
                onChange={(e) => updateField('stall_number', e.target.value)}
              />
            </label>

            <label className={authStyles.label}>
              Items / products for sale
              <textarea
                className={authStyles.textarea}
                value={form.items_for_sale}
                onChange={(e) => updateField('items_for_sale', e.target.value)}
                required
              />
            </label>

            <label className={styles.check}>
              <input
                type="checkbox"
                checked={form.content_approved}
                onChange={(e) => updateField('content_approved', e.target.checked)}
              />
              Content approved for public listing (products will not appear on calendars/public pages until
              checked)
            </label>

            <label className={styles.check}>
              <input
                type="checkbox"
                checked={form.electricity_needed}
                onChange={(e) => updateField('electricity_needed', e.target.checked)}
              />
              Electricity needed
            </label>

            <label className={authStyles.label}>
              Fee amount (R)
              <input
                className={authStyles.input}
                type="number"
                min="0"
                step="0.01"
                value={form.fee_amount}
                onChange={(e) => updateField('fee_amount', e.target.value)}
              />
            </label>

            <label className={authStyles.label}>
              Signatory name
              <input
                className={authStyles.input}
                value={form.signatory_name}
                onChange={(e) => updateField('signatory_name', e.target.value)}
                required
              />
            </label>

            <label className={authStyles.label}>
              Signed on
              <input
                className={authStyles.input}
                type="date"
                value={form.signed_on}
                onChange={(e) => updateField('signed_on', e.target.value)}
                required
              />
            </label>

            <label className={authStyles.label}>
              Status
              <select
                className={authStyles.select}
                value={form.status}
                onChange={(e) =>
                  updateField('status', e.target.value as EditForm['status'])
                }
              >
                <option value="submitted">Submitted</option>
                <option value="approved" disabled={!canApproveNow && form.status !== 'approved'}>
                  Approved
                  {!canApproveNow && form.status !== 'approved'
                    ? ' (needs form + indemnity + POP)'
                    : ''}
                </option>
                <option value="rejected">Rejected</option>
              </select>
            </label>
            {!canApproveNow ? (
              <p className={styles.meta}>
                Stall approval is blocked until the form is complete, indemnity is on file (digital
                signature or paper scan), and proof of payment is uploaded
                {!formLooksComplete ? ' · form incomplete' : ''}
                {!hasIndemnity ? ' · indemnity missing' : ''}
                {!application.has_payment_proof ? ' · POP missing' : ''}
                .
              </p>
            ) : null}

            <label className={authStyles.label}>
              Admin notes (internal)
              <textarea
                className={authStyles.textarea}
                value={form.admin_notes}
                onChange={(e) => updateField('admin_notes', e.target.value)}
              />
            </label>

            <div className={styles.actions}>
              <button className={authStyles.submit} type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>

          <div className={styles.detail} style={{ marginTop: '1.5rem' }}>
            <p>
              <strong>Paper indemnity:</strong>{' '}
              {application.has_paper_indemnity
                ? application.paper_indemnity_original_name || 'Uploaded'
                : 'Not uploaded'}
            </p>
            <p>
              <strong>Proof of payment:</strong>{' '}
              {application.has_payment_proof
                ? application.payment_proof_original_name || 'Uploaded'
                : 'Not uploaded'}
            </p>
            <p>
              <strong>Content moderation:</strong>{' '}
              {application.content_approved ? 'Approved for public listing' : 'Not approved yet'}
            </p>
            {application.signature_data ? (
              <img className={styles.signature} src={application.signature_data} alt="Vendor signature" />
            ) : (
              <p className={styles.meta}>No digital signature on this application.</p>
            )}
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primary}
                onClick={() => void downloadApplicationPdf(application)}
              >
                Download generated PDF
              </button>
              <button
                type="button"
                className={styles.primary}
                disabled={busy}
                onClick={() => openUploadPicker('indemnity')}
              >
                {application.has_paper_indemnity ? 'Replace paper indemnity' : 'Upload paper indemnity'}
              </button>
              {application.has_paper_indemnity ? (
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => void downloadPaperIndemnity(application)}
                >
                  Download paper indemnity
                </button>
              ) : null}
              <button
                type="button"
                className={styles.primary}
                disabled={busy}
                onClick={() => openUploadPicker('pop')}
              >
                {application.has_payment_proof ? 'Replace POP' : 'Upload POP'}
              </button>
              {application.has_payment_proof ? (
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => void downloadPaymentProof(application)}
                >
                  Download POP
                </button>
              ) : null}
            </div>
          </div>
        </>
      )}
    </section>
  )
}
